import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Switch, InputNumber, message, Spin, Typography, Divider, Tag, Space, Row, Col, List } from 'antd';
import { SettingOutlined, LockOutlined, MailOutlined, SafetyOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { getValidationPolicy, updateValidationPolicy, getAllowedDomains, updateAllowedDomains } from '../services/adminService';

const { Title, Text, Paragraph } = Typography;

function UserSettings() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [policyForm] = Form.useForm();

  const loadData = async () => {
    setLoading(true);
    try {
      const [p, d] = await Promise.all([
        getValidationPolicy(),
        getAllowedDomains(),
      ]);
      setPolicy(p);
      setDomains(d.domains || []);
      policyForm.setFieldsValue({
        password_min_length: p.password_min_length || 8,
        password_require_uppercase: p.password_require_uppercase ?? true,
        password_require_lowercase: p.password_require_lowercase ?? true,
        password_require_digit: p.password_require_digit ?? true,
        password_require_special_char: p.password_require_special_char ?? false,
      });
    } catch (err) {
      console.error('Failed to load settings:', err);
      message.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []); // eslint-disable-line

  const handleSavePolicy = async (values) => {
    setSaving(true);
    try {
      const updated = await updateValidationPolicy(values);
      setPolicy(updated);
      message.success('Validation policy updated');
    } catch (err) {
      message.error('Failed to update policy');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDomain = async () => {
    const domain = newDomain.trim().toLowerCase().replace(/^@/, '');
    if (!domain) return;
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(domain)) {
      message.error('Invalid domain format');
      return;
    }
    if (domains.includes(domain)) {
      message.warning('Domain already exists');
      return;
    }
    try {
      const updated = [...domains, domain];
      await updateAllowedDomains(updated);
      setDomains(updated);
      setNewDomain('');
      message.success('Domain added');
    } catch (err) {
      message.error('Failed to add domain');
    }
  };

  const handleRemoveDomain = async (domain) => {
    try {
      const updated = domains.filter(d => d !== domain);
      await updateAllowedDomains(updated);
      setDomains(updated);
      message.success('Domain removed');
    } catch (err) {
      message.error('Failed to remove domain');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 16 }}><SettingOutlined /> User Settings</Title>

      <Spin spinning={loading}>
        <Row gutter={24}>
          {/* Password & Account Policy */}
          <Col span={14}>
            <Card title={<><LockOutlined /> Password & Account Policy</>}>
              <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                Configure the validation rules applied when new users register.
              </Paragraph>
              <Form form={policyForm} layout="horizontal" labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}
                onFinish={handleSavePolicy}>

                <Divider orientation="left"><SafetyOutlined /> Password Rules</Divider>
                <Form.Item label="Minimum Password Length" name="password_min_length">
                  <InputNumber min={4} max={32} />
                </Form.Item>
                <Form.Item label="Require Uppercase" name="password_require_uppercase" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item label="Require Lowercase" name="password_require_lowercase" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item label="Require Digit" name="password_require_digit" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item label="Require Special Character" name="password_require_special_char" valuePropName="checked">
                  <Switch />
                </Form.Item>

                <Form.Item wrapperCol={{ offset: 12 }}>
                  <Button type="primary" htmlType="submit" loading={saving}>Save Policy</Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          {/* Email Domain Whitelist */}
          <Col span={10}>
            <Card title={<><MailOutlined /> Email Domain Whitelist</>}>
              <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                When domains are listed, only email addresses from these domains can register. Leave empty to allow all domains.
              </Paragraph>

              <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                <Input
                  placeholder="e.g. company.com"
                  value={newDomain}
                  onChange={e => setNewDomain(e.target.value)}
                  onPressEnter={handleAddDomain}
                />
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddDomain}>Add</Button>
              </Space.Compact>

              {domains.length > 0 ? (
                <List size="small" bordered dataSource={domains} renderItem={domain => (
                  <List.Item actions={[<Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleRemoveDomain(domain)} />]}>
                    <Tag color="blue">{domain}</Tag>
                  </List.Item>
                )} />
              ) : (
                <Text type="secondary">No domain restrictions — all email domains allowed.</Text>
              )}
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
}

export default UserSettings;
